import React, { useState } from "react";
import { Booking, type BookingInMessage } from "../../common/types/Booking";
import { emptyImage } from "../../utils/file.util";
import Overlay from "../Common/Overlay";
import "./BookingThumbnail.css";
import { isTypeOf } from "../../common/utils/zod.util";
import { DislikeEmoji, LikeEmoji } from "../Chat/EmojiReaction";
import { Flex } from "../Common/Flex";
import Icon, { PictogramIcon } from "../Common/Icon";
import MemberRating from "../Common/rating/MemberRating";
import BookingView from "./BookingView";

type Props = {
    booking: Booking | BookingInMessage | undefined;
    subtitle?: string | undefined;
    orderNumber?: number;
    bookingRating?: number | null | undefined;
    bookingFeedback?: string | null | undefined;
    className?: string;
    onClick?: () => void;
    onAdd?: () => void;
    onRemove?: () => void;
    hideProposalFeedback?: boolean;
    reviewNeeded?: boolean;
};

const BookingThumbnail = function BookingThumbnail({
    booking,
    subtitle,
    orderNumber,
    className,
    onClick,
    onAdd,
    onRemove,
    bookingRating,
    bookingFeedback,
    hideProposalFeedback = false,
    reviewNeeded,
}: Props) {
    const [bookingViewVisible, setBookingViewVisible] = useState(false);

    if (!booking) return <div />;
    return (
        <Flex.Row justifyContent="space-between" className={className}>
            <Flex.Row
                gap="medium"
                onClick={() => {
                    if (onClick) {
                        onClick();
                    } else {
                        setBookingViewVisible(true);
                    }
                }}
                className="booking-thumbnail"
            >
                <div>
                    <div className="booking-thumbnail__image">
                        <img
                            className="booking-thumbnail__image-thumb"
                            src={
                                booking.croppedImageURL ||
                                booking.imageURL ||
                                emptyImage
                            }
                            alt={booking?.title || ""}
                        />
                        {booking.reservationStatus === "confirmed" && (
                            <PictogramIcon.Check.Small.P90 className="booking-thumbnail__image-confirmed" />
                        )}
                        {booking.reviewNeeded && (
                            <PictogramIcon.PriorityHigh.Small.E90 className="booking-thumbnail__review-needed" />
                        )}
                        {booking.reviewNeeded &&
                            booking.reservationStatus === "confirmed" && (
                                <PictogramIcon.PriorityHigh.Small.S90 className="booking-thumbnail__review-needed" />
                            )}

                        {orderNumber && (
                            <div className="booking-thumbnail__order-number">
                                {orderNumber}
                            </div>
                        )}
                    </div>
                </div>

                <Flex.Column>
                    {booking.title ? (
                        <span className="booking-thumbnail__title">
                            {booking.title}
                        </span>
                    ) : (
                        <span className="booking-thumbnail__placeholder">
                            New option...
                        </span>
                    )}

                    {subtitle && (
                        <div className="booking-thumbnail__subtitle">{subtitle}</div>
                    )}

                    {bookingRating && (
                        <div className="booking-thumbnail__rating">
                            <MemberRating
                                maxRating={5}
                                userRating={bookingRating}
                                message={bookingFeedback}
                            />
                        </div>
                    )}
                </Flex.Column>
            </Flex.Row>

            <div>
                {!hideProposalFeedback && (
                    <>
                        {isTypeOf(booking, Booking) &&
                            booking.proposalFeedback === "like" && <LikeEmoji />}
                        {isTypeOf(booking, Booking) &&
                            booking.proposalFeedback === "dislike" && <DislikeEmoji />}
                    </>
                )}
                {!reviewNeeded && onAdd && (
                    <div onClick={onAdd}>
                        <Icon.Logout.Small className="booking-thumbnail__add-to-chat" />
                    </div>
                )}
                {onRemove && (
                    <div onClick={onRemove}>
                        <Icon.Close.Small className="booking-thumbnail__remove" />
                    </div>
                )}
            </div>

            {bookingViewVisible && (
                <Overlay
                    fullsize
                    onClose={() => {
                        setBookingViewVisible(false);
                        return false;
                    }}
                >
                    {" "}
                    <BookingView booking={booking} orderNumber={orderNumber} />
                </Overlay>
            )}
        </Flex.Row>
    );
};

export default BookingThumbnail;
